import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { searchEvents, getFilterOptions, getFeaturedEvent } from '../services/eventService';
import { useLocale } from '../i18n/LocaleContext';
import AppHeader from '../components/AppHeader';
import DiscoveryBar from '../components/DiscoveryBar';
import FilterDrawer from '../components/FilterDrawer';
import ActiveFilters from '../components/ActiveFilters';
import EventCard from '../components/EventCard';
import { EmptyState, ErrorState, SkeletonGrid } from '../components/States';
import ScrollToTop from '../components/ScrollToTop';
import { buildCategoryOptions } from '../utils/eventCategories';
import { buildMusicGroupOptions } from '../utils/musicGroups';
import {
  EMPTY_FILTERS,
  applyQuickDate,
  countActiveFilters,
  hasAnyFilter,
  toggleInList
} from '../utils/filters';
import { formatDayHeading, parseEventDate, toDayKey } from '../utils/format';
import { DEFAULT_SORT_OPTION, getSortOptionById } from '../utils/sortOptions';
import { requestUserLocation } from '../utils/geolocation';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { readHomeSnapshot, saveHomeSnapshot } from '../utils/homeSnapshot';
import { useHideOnScroll } from '../utils/useHideOnScroll';
import {
  readStoredCityFilter,
  resolveActiveCity,
  storeCityFilter,
  storeCityLabel
} from '../utils/city';

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 350;

// Groups the loaded page(s) by calendar day so a long list reads as an agenda
// rather than an undifferentiated wall of cards.
function groupByDay(events) {
  const groups = [];
  let current = null;

  for (const event of events) {
    const key = toDayKey(parseEventDate(event.date));
    if (!current || current.key !== key) {
      current = { key, date: event.date, events: [] };
      groups.push(current);
    }
    current.events.push(event);
  }

  return groups;
}

export default function HomePage() {
  const navigate = useNavigate();
  const { locale, t } = useLocale();

  useDocumentTitle(`${t('app.name')} — ${t('app.tagline')}`);

  // Coming Back to this history entry restores the list as the visitor left it
  // (see utils/homeSnapshot.js); a new visit finds no snapshot and starts fresh.
  const location = useLocation();
  const [restored] = useState(() => readHomeSnapshot(location.key));
  const restoredResults = restored?.results;

  const [events, setEvents] = useState(() => restoredResults?.events ?? []);
  const [pagination, setPagination] = useState(() => restoredResults?.pagination ?? null);
  const [status, setStatus] = useState(restoredResults ? 'ready' : 'loading'); // loading | ready | error
  const [loadingMore, setLoadingMore] = useState(false);

  const [searchInput, setSearchInput] = useState(() => restored?.searchInput ?? '');
  const [searchQuery, setSearchQuery] = useState(() => restored?.searchQuery ?? '');
  const [filters, setFilters] = useState(() => restored?.filters ?? {
    ...EMPTY_FILTERS,
    city: readStoredCityFilter()
  });
  const [sortOptionId, setSortOptionId] = useState(
    () => restored?.sortOptionId ?? DEFAULT_SORT_OPTION.id
  );
  const [userLocation, setUserLocation] = useState(() => restored?.userLocation ?? null);
  const [locationError, setLocationError] = useState('');
  const [noticeKey, setNoticeKey] = useState('');

  const [filterOptions, setFilterOptions] = useState(() => restored?.filterOptions ?? {
    musicGenres: [],
    musicGroups: [],
    eventTypes: [],
    cities: [],
    countries: [],
    venues: [],
    specialFeatures: [],
    districts: [],
    priceRange: { min: 0, max: 0 },
    totals: { total: 0, free: 0 }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Part of the Back snapshot: popping in after the restore would push the list
  // down and spoil the restored scroll position.
  const [featured, setFeatured] = useState(() => restored?.featured ?? null);

  // Guards against an older in-flight request overwriting a newer result.
  const requestId = useRef(0);
  const sentinelRef = useRef(null);
  // The request the restored results answer. While the filters still match it,
  // re-fetching would throw away the extra pages and the scroll position.
  const restoredRequestKey = useRef(restoredResults ? restored.requestKey : null);

  useEffect(() => {
    getFilterOptions().then(setFilterOptions);
    getFeaturedEvent().then(setFeatured);
  }, []);

  const buildRequest = useCallback(
    (page) => {
      const sortOption = getSortOptionById(sortOptionId);
      const request = {
        ...filters,
        search: searchQuery || undefined,
        sortBy: sortOption.sortBy,
        sortOrder: sortOption.sortOrder,
        page,
        limit: PAGE_SIZE
      };

      if (userLocation) {
        request.userLat = userLocation.latitude;
        request.userLng = userLocation.longitude;
      }

      return request;
    },
    [filters, searchQuery, sortOptionId, userLocation]
  );

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setStatus('loading');
    setNoticeKey('');

    try {
      const data = await searchEvents(buildRequest(1));
      if (id !== requestId.current) {
        return;
      }
      setEvents(data.events || []);
      setPagination(data.pagination || null);
      setStatus('ready');
    } catch (error) {
      if (id !== requestId.current) {
        return;
      }
      if (error.response?.status === 400) {
        setNoticeKey('location.denied');
        setStatus('ready');
        return;
      }
      console.error('Error loading events:', error);
      setStatus('error');
    }
  }, [buildRequest]);

  useEffect(() => {
    if (JSON.stringify(buildRequest(1)) === restoredRequestKey.current) {
      return;
    }
    restoredRequestKey.current = null;
    load();
  }, [buildRequest, load]);

  // The state to hand back on Back, refreshed after every render.
  const latest = useRef(null);
  useEffect(() => {
    latest.current = {
      historyKey: location.key,
      filters,
      searchInput,
      searchQuery,
      sortOptionId,
      userLocation,
      filterOptions,
      featured,
      requestKey: JSON.stringify(buildRequest(1)),
      results: status === 'ready' ? { events, pagination } : null
    };
  });

  // A layout-effect cleanup runs before the next page replaces this one in the
  // DOM, so window.scrollY is still the list's position.
  useLayoutEffect(
    () => () => {
      if (latest.current) {
        const { historyKey, ...snapshot } = latest.current;
        saveHomeSnapshot(historyKey, { ...snapshot, scrollY: window.scrollY });
      }
    },
    []
  );

  useLayoutEffect(() => {
    if (restoredResults) {
      window.scrollTo(0, restored.scrollY);
    }
  }, [restored, restoredResults]);

  // Typing searches on its own; the button is there for muscle memory only.
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadMore = useCallback(async () => {
    if (!pagination?.hasMore || loadingMore || status !== 'ready') {
      return;
    }

    setLoadingMore(true);
    const id = requestId.current;

    try {
      const data = await searchEvents(buildRequest(pagination.page + 1));
      if (id !== requestId.current) {
        return;
      }
      setEvents((current) => [...current, ...(data.events || [])]);
      setPagination(data.pagination || null);
    } catch (error) {
      console.error('Error loading more events:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [buildRequest, loadingMore, pagination, status]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !pagination?.hasMore) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '600px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, pagination]);

  const handleSortChange = async (nextId) => {
    const option = getSortOptionById(nextId);

    if (option.requiresLocation && !userLocation) {
      try {
        setLocationError('');
        setUserLocation(await requestUserLocation(t));
        setSortOptionId(nextId);
      } catch (error) {
        setLocationError(error.message);
      }
      return;
    }

    setSortOptionId(nextId);
  };

  const removeFilter = (key, value) => {
    if (key === 'dates') {
      setFilters((current) => ({
        ...current,
        dateFrom: undefined,
        dateTo: undefined,
        quickDate: undefined
      }));
      return;
    }
    if (Array.isArray(filters[key])) {
      setFilters((current) => ({ ...current, [key]: toggleInList(current[key], value) }));
      return;
    }
    setFilters((current) => ({ ...current, [key]: key === 'freeOnly' ? false : undefined }));
  };

  const resetAll = () => {
    storeCityFilter(undefined);
    setFilters(EMPTY_FILTERS);
    setSearchInput('');
    setSearchQuery('');
    setDrawerOpen(false);
  };

  const categories = useMemo(
    () => buildCategoryOptions(filterOptions.eventTypes),
    [filterOptions.eventTypes]
  );
  const musicGroups = useMemo(
    () => buildMusicGroupOptions(filterOptions.musicGroups),
    [filterOptions.musicGroups]
  );

  const activeCity = resolveActiveCity(filters.city, filterOptions.cities);

  useEffect(() => {
    storeCityLabel(activeCity);
  }, [activeCity]);

  const handleCityChange = (city) => {
    storeCityFilter(city);
    setFilters((current) => ({ ...current, city }));
  };

  const barsHidden = useHideOnScroll();

  // The highlighted event leads the start page. It steps aside while the
  // visitor searches or filters - a concert has no place above theatre
  // results - or browses a different city, and is not listed twice.
  const showFeatured = Boolean(featured)
    && !searchQuery
    && countActiveFilters({ ...filters, city: undefined }) === 0
    && (!filters.city || featured.city === filters.city);
  const listEvents = useMemo(
    () => (showFeatured ? events.filter((event) => event._id !== featured._id) : events),
    [events, featured, showFeatured]
  );

  const dayGroups = useMemo(() => groupByDay(listEvents), [listEvents]);
  const activeFilterCount = countActiveFilters(filters);
  const groupByDate = getSortOptionById(sortOptionId).sortBy === 'date';

  return (
    <div className="app">
      <AppHeader
        cityLabel={activeCity}
        cities={filterOptions.cities}
        selectedCity={filters.city}
        onCityChange={handleCityChange}
        hidden={barsHidden}
      />

      <DiscoveryBar
        searchQuery={searchInput}
        onSearchQueryChange={setSearchInput}
        onSubmitSearch={() => setSearchQuery(searchInput.trim())}
        categories={categories}
        selectedCategories={filters.categories}
        onToggleCategory={(id) =>
          setFilters((current) => ({ ...current, categories: toggleInList(current.categories, id) }))
        }
        musicGroups={musicGroups}
        selectedMusicGroups={filters.musicGroups || []}
        onToggleMusicGroup={(id) =>
          setFilters((current) => ({
            ...current,
            musicGroups: toggleInList(current.musicGroups || [], id)
          }))
        }
        quickDate={filters.quickDate}
        onQuickDate={(id) => setFilters((current) => applyQuickDate(current, id))}
        freeOnly={filters.freeOnly}
        onToggleFree={() => setFilters((current) => ({ ...current, freeOnly: !current.freeOnly }))}
        sortOptionId={sortOptionId}
        onSortChange={handleSortChange}
        activeFilterCount={activeFilterCount}
        onOpenFilters={() => setDrawerOpen(true)}
        loading={status === 'loading'}
        hidden={barsHidden}
      />

      <main className="page">
        <ActiveFilters
          filters={filters}
          searchQuery={searchQuery}
          onRemove={removeFilter}
          onClearSearch={() => {
            setSearchInput('');
            setSearchQuery('');
          }}
        />

        {(noticeKey || locationError) && (
          <p className="notice">{locationError || t(noticeKey)}</p>
        )}

        {showFeatured && (
          <section className="featured">
            <h2 className="day-group__heading">{t('featured.title')}</h2>
            <EventCard
              event={featured}
              featured
              onClick={() => navigate(`/event/${featured._id}`)}
            />
          </section>
        )}

        <div className="results-bar">
          <span className="results-bar__count">
            {status === 'loading' && events.length === 0
              ? t('results.loading')
              : t('results.count', { count: pagination?.total ?? 0 })}
          </span>
        </div>

        {status === 'error' ? (
          <ErrorState onRetry={load} />
        ) : status === 'loading' && events.length === 0 ? (
          <SkeletonGrid count={6} />
        ) : events.length === 0 ? (
          <EmptyState onReset={hasAnyFilter(filters, searchQuery) ? resetAll : undefined} />
        ) : groupByDate ? (
          dayGroups.map((group) => (
            <section className="day-group" key={group.key}>
              <h2 className="day-group__heading">{formatDayHeading(group.date, locale)}</h2>
              <div className="events-grid">
                {group.events.map((event) => (
                  <EventCard
                    key={event._id}
                    event={event}
                    onClick={() => navigate(`/event/${event._id}`)}
                  />
                ))}
              </div>
            </section>
          ))
        ) : (
          <div className="events-grid">
            {listEvents.map((event) => (
              <EventCard
                key={event._id}
                event={event}
                onClick={() => navigate(`/event/${event._id}`)}
              />
            ))}
          </div>
        )}

        <div ref={sentinelRef} />

        {events.length > 0 && (
          <div className="load-more">
            {pagination?.hasMore ? (
              <button
                type="button"
                className="btn"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? t('results.loading') : t('results.loadMore')}
              </button>
            ) : (
              <span className="load-more__end">{t('results.end')}</span>
            )}
          </div>
        )}
      </main>

      <ScrollToTop />

      <FilterDrawer
        open={drawerOpen}
        filters={filters}
        filterOptions={filterOptions}
        searchQuery={searchQuery}
        onApply={(next) => {
          setFilters(next);
          setDrawerOpen(false);
        }}
        onClear={resetAll}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
